import React, { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import UIVisualizador from './UIVisualizador';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragStartEvent,
    DragOverlay,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './UIFotoUploadV2.css';

interface Foto {
    id: string;
    source: string | null;
    file: File | null;
    tipo: string;
    ordem: number;
    uploading: boolean;
    preview?: string; // URL temporária (blob) ou caminho do thumbnail no servidor
}

interface UIFotoUploadV2Props {
    name: string;
    contId?: number | string;
    uploadRoute?: string;
    idParam?: string;
    showTipo?: boolean;
    dropzoneLast?: boolean;
}

// Lista de tipos de fotos disponíveis
const TIPOS_FOTO = [
    'Padrão de Entrada',
    'Etiqueta do Inversor',
    'Inversor',
    'Etiqueta do Módulo Fotovoltaico',
    'Módulo Fotovoltaico',
    'Disjuntor',
    'Aterramento',
    'String Box',
    'Quadro Elétrico',
    'Telhas/Telhado',
    'Estrutura de Fixação',
    'Datalogger/Monitoramento',
    'Vista Geral',
    'Outros',
];

// Função para gerar thumbnail de uma imagem
const generateThumbnail = (file: File, maxWidth: number = 400, maxHeight: number = 400): Promise<Blob> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                // Calcula as dimensões mantendo a proporção
                let width = img.width;
                let height = img.height;

                if (width > height) {
                    if (width > maxWidth) {
                        height = (height * maxWidth) / width;
                        width = maxWidth;
                    }
                } else {
                    if (height > maxHeight) {
                        width = (width * maxHeight) / height;
                        height = maxHeight;
                    }
                }

                // Cria canvas e redimensiona
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');

                if (!ctx) {
                    reject(new Error('Failed to get canvas context'));
                    return;
                }

                ctx.drawImage(img, 0, 0, width, height);

                // Converte para blob
                canvas.toBlob((blob) => {
                    if (blob) {
                        resolve(blob);
                    } else {
                        reject(new Error('Failed to create blob'));
                    }
                }, 'image/jpeg', 0.85);
            };
            img.onerror = () => reject(new Error('Failed to load image'));
            img.src = e.target?.result as string;
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
    });
};

// Componente de um item de foto com drag & drop
function FotoItem({ foto, onRemove, onChangeTipo, onClick, showTipo = true }: {
    foto: Foto;
    onRemove: () => void;
    onChangeTipo: (tipo: string) => void;
    onClick: () => void;
    showTipo?: boolean;
}) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: foto.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    // Usa preview (que pode ser blob temporário ou thumbnail do servidor) ou source original
    let imageUrl = foto.preview || foto.source || '';

    // Se não for blob nem URL completa, adiciona prefixo da API
    if (imageUrl && !imageUrl.startsWith('blob:') && !imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
        imageUrl = `${process.env.REACT_APP_API_ENDPOINT}/${imageUrl}`;
    }

    // Determina o título da foto (nome do arquivo)
    let fotoTitulo = `Foto ${foto.ordem + 1}`;
    if (foto.file) {
        fotoTitulo = foto.file.name;
    } else if (foto.source) {
        // Extrai o nome do arquivo do path
        const parts = foto.source.split('/');
        fotoTitulo = parts[parts.length - 1];
    }

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`foto-item ${isDragging ? 'dragging' : ''}`}
        >
            <div className="foto-drag-handle" {...attributes} {...listeners}>
                <svg width="16" height="16" viewBox="0 0 20 20">
                    <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z" />
                </svg>
            </div>

            <button
                type="button"
                className="foto-remove-btn"
                onClick={(e) => {
                    e.stopPropagation();
                    onRemove();
                }}
                disabled={foto.uploading}
                title="Remover foto"
            >
                <svg viewBox="0 0 16 16">
                    <path d="M3.72 3.72a.75.75 0 0 1 1.06 0L8 6.94l3.22-3.22a.75.75 0 1 1 1.06 1.06L9.06 8l3.22 3.22a.75.75 0 1 1-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 0 1-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 0 1 0-1.06z" />
                </svg>
            </button>

            <div className="foto-preview" onClick={onClick} style={{ cursor: 'pointer' }}>
                {imageUrl ? (
                    <img src={imageUrl} alt={fotoTitulo} />
                ) : (
                    <div className="foto-placeholder">Carregando...</div>
                )}
                {foto.uploading && (
                    <div className="foto-uploading-overlay">
                        <div className="spinner"></div>
                        <div className="foto-upload-progress">
                            Enviando foto...
                        </div>
                    </div>
                )}
            </div>

            <div className="foto-controls">
                <div className="foto-title" title={fotoTitulo}>
                    {fotoTitulo}
                </div>

                {showTipo && (
                    <div className="foto-field">
                        <label>Tipo:</label>
                        <select
                            value={foto.tipo}
                            onChange={(e) => onChangeTipo(e.target.value)}
                            disabled={foto.uploading}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <option value="">Selecione o tipo</option>
                            {TIPOS_FOTO.map(tipo => (
                                <option key={tipo} value={tipo}>{tipo}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>
        </div>
    );
}

export default function UIFotoUploadV2({ name, contId, uploadRoute = 'contrato/form&action=fotoUpload', idParam = 'cont_id', showTipo = true, dropzoneLast = false }: UIFotoUploadV2Props) {
    const formContext = useFormContext();
    const { register, watch, setValue } = formContext;

    // Estado local para gerenciar fotos
    const [fotos, setFotos] = useState<Foto[]>([]);
    const [activeDragId, setActiveDragId] = useState<string | null>(null);
    const [showVisualizador, setShowVisualizador] = useState(false);
    const [currentFotoIndex, setCurrentFotoIndex] = useState(0);

    // Inicializar fotos do form
    useEffect(() => {
        register(name);
        const formValue = watch(name);
        if (formValue && Array.isArray(formValue)) {
            const fotosIniciais: Foto[] = formValue.map((f: any, index: number) => ({
                id: f.id || `foto-${Date.now()}-${index}`,
                source: f.source || null,
                file: null,
                tipo: f.tipo || '',
                ordem: f.ordem !== undefined ? f.ordem : index,
                uploading: false,
                preview: f.thumbnail || f.source || null, // Usa thumbnail se existir, senão source
            }));
            setFotos(fotosIniciais);
        }
    }, []);

    // Sincronizar fotos com form
    useEffect(() => {
        const formData = fotos.map((foto, index) => ({
            id: foto.id,
            source: foto.source,
            // Thumbnail é o preview se não for blob temporário
            thumbnail: foto.preview && !foto.preview.startsWith('blob:') ? foto.preview : null,
            tipo: foto.tipo,
            ordem: index,
        })).filter(f => f.source); // Só inclui fotos já enviadas

        // IMPORTANTE: Sempre enviar array (mesmo vazio) para garantir que remoção total funcione
        // Com shouldTouch: true, o React Hook Form sempre inclui o campo no submit
        setValue(name, formData, {
            shouldDirty: true,
            shouldTouch: true,  // Marca como "tocado" para garantir envio
            shouldValidate: false  // Não valida, apenas atualiza
        });
    }, [fotos, name, setValue]);

    // Upload de arquivo para o servidor
    const uploadFile = async (file: File, fotoId: string) => {
        try {
            // Gera thumbnail
            const thumbnailBlob = await generateThumbnail(file);

            const formData = new FormData();
            formData.append('files', file);
            formData.append('thumbnail', thumbnailBlob, 'thumb_' + file.name);
            if (contId) {
                formData.append(idParam, String(contId));
            }

            const response = await fetch(
                `${process.env.REACT_APP_API_ENDPOINT}/?route=${uploadRoute}`,
                {
                    method: 'POST',
                    body: formData,
                    credentials: 'include',
                }
            );

            const result = await response.json();

            if (result.status === 'success' && result.payload) {
                // Atualiza foto com source e preview (thumbnail) retornados
                setFotos(prev => prev.map(f =>
                    f.id === fotoId
                        ? {
                            ...f,
                            source: result.payload.source || result.payload,
                            preview: result.payload.thumbnail || result.payload.source || result.payload,
                            uploading: false
                        }
                        : f
                ));
            } else {
                throw new Error(result.message || 'Erro ao enviar arquivo');
            }
        } catch (error) {
            console.error('Erro no upload:', error);
            alert('Erro ao enviar arquivo: ' + (error instanceof Error ? error.message : 'Erro desconhecido'));
            // Remove foto que falhou
            setFotos(prev => prev.filter(f => f.id !== fotoId));
        }
    };

    // Callback do dropzone
    const onDrop = (acceptedFiles: File[]) => {
        const novasFotos: Foto[] = acceptedFiles.map((file, index) => {
            const fotoId = `foto-${Date.now()}-${index}`;
            const preview = URL.createObjectURL(file);

            const foto: Foto = {
                id: fotoId,
                source: null,
                file,
                tipo: '',
                ordem: fotos.length + index,
                uploading: true,
                preview,
            };

            // Inicia upload
            uploadFile(file, fotoId);

            return foto;
        });

        setFotos(prev => [...prev, ...novasFotos]);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
        },
        multiple: true,
    });

    // Sensores para drag & drop
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Handle drag start
    const handleDragStart = (event: DragStartEvent) => {
        setActiveDragId(event.active.id as string);
    };

    // Handle drag end
    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            setFotos((items) => {
                const oldIndex = items.findIndex(item => item.id === active.id);
                const newIndex = items.findIndex(item => item.id === over.id);
                return arrayMove(items, oldIndex, newIndex);
            });
        }

        setActiveDragId(null);
    };

    // Remover foto
    const handleRemove = (fotoId: string) => {
        setFotos(prev => prev.filter(f => f.id !== fotoId));
    };

    // Mudar tipo
    const handleChangeTipo = (fotoId: string, tipo: string) => {
        setFotos(prev => prev.map(f =>
            f.id === fotoId ? { ...f, tipo } : f
        ));
    };

    // Abrir visualizador
    const handleFotoClick = (fotoId: string) => {
        const index = fotos.findIndex(f => f.id === fotoId);
        if (index !== -1) {
            setCurrentFotoIndex(index);
            setShowVisualizador(true);
        }
    };

    const activeFoto = activeDragId ? fotos.find(f => f.id === activeDragId) : null;

    const dropzoneEl = (
        <div
            {...getRootProps()}
            className={`dropzone ${isDragActive ? 'active' : ''}`}
        >
            <input {...getInputProps()} />
            <div className="dropzone-content">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                    <path d="M12 15V3m0 12l-4-4m4 4l4-4M2 17l.621 2.485A2 2 0 0 0 4.561 21h14.878a2 2 0 0 0 1.94-1.515L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {isDragActive ? (
                    <p>Solte as imagens aqui...</p>
                ) : (
                    <p>Arraste imagens aqui ou clique para selecionar</p>
                )}
            </div>
        </div>
    );

    const listEl = fotos.length > 0 && (
        <SortableContext
            items={fotos.map(f => f.id)}
            strategy={rectSortingStrategy}
        >
            {fotos.map((foto) => (
                <FotoItem
                    key={foto.id}
                    foto={foto}
                    onRemove={() => handleRemove(foto.id)}
                    onChangeTipo={(tipo) => handleChangeTipo(foto.id, tipo)}
                    onClick={() => handleFotoClick(foto.id)}
                    showTipo={showTipo}
                />
            ))}
        </SortableContext>
    );

    return (
        <div className="ui-foto-upload-v2">
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
            >
                <div className="fotos-container">
                    {dropzoneLast ? (
                        <>{listEl}{dropzoneEl}</>
                    ) : (
                        <>{dropzoneEl}{listEl}</>
                    )}
                </div>

                {/* Drag Overlay - Outline pontilhado grudado no mouse */}
                <DragOverlay>
                    {activeFoto && (
                        <div className="foto-drag-overlay" />
                    )}
                </DragOverlay>
            </DndContext>

            <UIVisualizador
                show={showVisualizador}
                onClose={() => setShowVisualizador(false)}
                tipo="foto"
                items={fotos.map(f => ({ source: f.source || '', tipo: f.tipo }))}
                currentIndex={currentFotoIndex}
            />
        </div>
    );
}
